/**
 * Grouping anything repository-shaped, newest activity first.
 *
 * Extracted when the pull request list needed the same shape as the session
 * tree. The two render nothing alike, but the part worth getting right is
 * identical and easy to get subtly wrong twice: the owner belongs in the
 * identity while only the name is shown, ordering is by recency at both levels,
 * and an unparseable timestamp must sort last rather than return NaN from a
 * comparator and scramble the whole list.
 *
 * @module lib/group-by-repo
 */

/** What the grouper needs from an item; everything else is the caller's. */
export interface RepoKeyed {
  repoOwner: string
  repoName: string
  /** Activity timestamp, ISO. Null falls back to `createdAt`-style ordering. */
  activityAt: string | null
}

export interface RepoGroup<T> {
  /** `owner/name` — stable, so it keys collapse state and React lists. */
  key: string
  repoOwner: string
  /**
   * The label. Repository only, never `owner/name`.
   *
   * The owner is the same organisation on nearly every row, so printing it
   * spends the narrowest dimension on the least distinguishing part of a name.
   */
  repoName: string
  items: T[]
  /** Most recent activity in the group; the sort key. */
  lastActivity: number
}

/** Epoch millis for ordering; 0 for anything unparseable, so it sorts last. */
function timeOf(stamp: string | null): number {
  if (stamp === null) return 0
  const parsed = new Date(stamp).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

/**
 * Groups by repository, newest work first at both levels.
 *
 * Recency rather than alphabetical: both callers exist for resuming or
 * reviewing, and the thing touched last is overwhelmingly the one wanted.
 * Alphabetical is stable, which reads as tidier and is worse — it buries
 * today's work under a repository whose name happens to start with "a".
 */
export function groupByRepo<T>(items: T[], keyOf: (item: T) => RepoKeyed): Array<RepoGroup<T>> {
  const groups = new Map<string, RepoGroup<T>>()

  for (const item of items) {
    const { repoOwner, repoName, activityAt } = keyOf(item)
    const key = `${repoOwner}/${repoName}`
    const at = timeOf(activityAt)

    const existing = groups.get(key)
    if (existing === undefined) {
      groups.set(key, { key, repoOwner, repoName, items: [item], lastActivity: at })
      continue
    }
    existing.items.push(item)
    existing.lastActivity = Math.max(existing.lastActivity, at)
  }

  const ordered = [...groups.values()]
  for (const group of ordered) {
    group.items.sort((left, right) => timeOf(keyOf(right).activityAt) - timeOf(keyOf(left).activityAt))
  }
  ordered.sort((left, right) => right.lastActivity - left.lastActivity)
  return ordered
}
